import React from "react";
import { motion } from "framer-motion";
import {
  Shield,
  HardHat,
  FileCheck2,
  Receipt,
  UserCheck,
  Wrench,
  Siren,
  ClipboardList,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/site/PageHero";

const compliance = [
  {
    id: "nrta",
    icon: Shield,
    title: "National Road Traffic Act",
    desc: "Roadworthy, permitted and licensed for every route they run.",
  },
  {
    id: "ohsa",
    icon: HardHat,
    title: "Occupational Health & Safety",
    desc: "Driver wellbeing, load safety and incident protocols vetted before any trip.",
  },
  {
    id: "insurance",
    icon: FileCheck2,
    title: "Industry-Specific Insurance",
    desc: "Goods-in-transit and liability cover matched to each commodity, route and value.",
  },
  {
    id: "sars",
    icon: Receipt,
    title: "SARS Tax Compliance",
    desc: "Tax-compliant operators with valid SARS standing — clean audits, every time.",
  },
];

const operational = [
  {
    id: "driver",
    icon: UserCheck,
    title: "Driver vetting",
    desc: "Valid PrDP, medical clearance, criminal record check and a proven driving history before a single load is allocated.",
  },
  {
    id: "vehicle",
    icon: Wrench,
    title: "Vehicle inspections",
    desc: "Pre-trip mechanical inspections, valid CoF, GIT-rated trailers and operating-licence verification — re-checked annually.",
  },
  {
    id: "incident",
    icon: Siren,
    title: "Incident response",
    desc: "24/7 incident desk, escalation matrix and shipper notification within 30 minutes of any reported event.",
  },
  {
    id: "audit",
    icon: ClipboardList,
    title: "Continuous audit",
    desc: "Carrier scorecards on safety, on-time performance and compliance — re-audited every 12 months without exception.",
  },
];

const policy = [
  "Zero tolerance on alcohol, drugs and unauthorised passengers",
  "Mandatory rest breaks aligned to NRTA driving-hour limits",
  "Speed governors active on all heavy & abnormal vehicles",
  "Hazardous goods only handled by SAQA-accredited DG drivers",
  "Real-time vehicle tracking on every multi-day or cross-border move",
  "Full chain-of-responsibility audit trail kept for every consignment",
];

export default function Safety() {
  return (
    <>
      <PageHero
        eyebrow="Safety & compliance"
        badge={<><Shield className="h-3.5 w-3.5" /> Safety first · always</>}
        title="Safety is not a checkbox."
        accent="It's the brief."
        description="Every load we broker rides on the back of a carrier we've vetted, audited and trusted. From driver paperwork to incident response, here's how we keep Southern Africa's freight — and the people moving it — safe."
        primaryCta={{ label: "Talk to our safety team", to: "/contact", testId: "safety-cta-contact" }}
        secondaryCta={{ label: "Become a vetted carrier", to: "/contact", testId: "safety-cta-carrier" }}
        image="https://images.unsplash.com/photo-1565891741441-64926e441838?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
      />

      {/* Compliance pillars */}
      <section className="relative py-20 md:py-28 bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              The four non-negotiables
            </div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              Every carrier on our network is compliant on four fronts.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {compliance.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.id}
                  data-testid={`safety-compliance-${c.id}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="bg-white rounded-2xl border border-[#E2E8F0] p-7 hover:border-[#0070C0]/40 hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(0,32,80,0.3)] transition-all"
                >
                  <div className="h-11 w-11 rounded-xl bg-[#0070C0]/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[#0070C0]" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display mt-5 text-lg font-bold text-[#002050]">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#1F2937]/75 leading-relaxed">
                    {c.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Operational safety */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
                Operational safety
              </div>
              <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
                Beyond paperwork — how we actually keep loads safe.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-[#1F2937]/75 leading-relaxed">
                Compliance gets you onto our network. Discipline keeps you on
                it. Every move is wrapped in four operational checks.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {operational.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.div
                  key={o.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.45, delay: (i % 2) * 0.08 }}
                  className="bg-white rounded-2xl border border-[#E2E8F0] p-7 md:p-8 hover:border-[#0070C0]/40 hover:shadow-[0_20px_50px_-25px_rgba(0,32,80,0.25)] transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-[#002050] flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[#00AEEF]" strokeWidth={1.75} />
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-bold text-[#002050] tracking-tight">
                      {o.title}
                    </h3>
                  </div>
                  <p className="mt-4 text-[15px] text-[#1F2937]/75 leading-relaxed">
                    {o.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Policy stripe */}
      <section className="relative py-20 md:py-28 bg-[#002050] overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-50" />
        <div className="absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-[#0070C0]/30 blur-3xl" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#00AEEF]">
              Our safety policy
            </div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter text-white leading-[1.05]">
              The non-negotiables every carrier signs.
            </h2>
            <p className="mt-5 text-lg text-white/75 leading-relaxed">
              Before a carrier moves their first load with us, they sign our
              code of conduct. These six rules sit at the top of it.
            </p>
          </div>

          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {policy.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur"
                >
                  <CheckCircle2 className="h-5 w-5 text-[#00AEEF] shrink-0 mt-0.5" />
                  <span className="text-white/90 text-[15px] leading-snug">
                    {p}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="rounded-3xl border border-[#E2E8F0] bg-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-[0_30px_80px_-30px_rgba(0,32,80,0.18)]">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0070C0]">
                Questions about safety?
              </div>
              <h3 className="font-display mt-2 text-2xl md:text-3xl font-bold text-[#002050]">
                We're happy to share our full carrier compliance pack.
              </h3>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#0070C0] hover:bg-[#002050] text-white px-7 py-3.5 rounded-xl font-bold text-sm transition-colors group"
            >
              Contact our safety team
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
